import { useReceivedOrders, ReceivedOrdersUrgency } from '../contexts/ReceivedOrdersContext'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

const CASH_REGISTRY_PERMISSIONS = [PERMISSIONS.ORDER_CHECKOUT, PERMISSIONS.PAYMENT_CASH_CREATE]

const TONES: Record<Exclude<ReceivedOrdersUrgency, 'idle'>, { container: string; pulse: boolean }> = {
  calm: {
    container: 'bg-blue-50 text-blue-900 border-blue-200 hover:bg-blue-100',
    pulse: false,
  },
  warning: {
    container: 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100',
    pulse: false,
  },
  urgent: {
    container: 'bg-red-100 text-red-900 border-red-300 hover:bg-red-200',
    pulse: true,
  },
}

const formatAge = (minutes: number | null): string => {
  if (minutes === null || minutes < 1) return ''
  if (minutes < 60) return `${minutes}m`
  return `${Math.floor(minutes / 60)}h`
}

const IncomingOrdersBanner = () => {
  const { count, oldestAgeMinutes, urgency, openSheet } = useReceivedOrders()
  const { hasAnyPermission } = usePermissions()

  if (!hasAnyPermission(CASH_REGISTRY_PERMISSIONS)) return null
  if (count === 0 || urgency === 'idle') return null

  const tone = TONES[urgency]
  const ageLabel = formatAge(oldestAgeMinutes)

  return (
    <button
      type="button"
      onClick={openSheet}
      className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-3 border-b px-4 py-2 text-sm font-medium transition-colors ${tone.container}`}
      aria-label={`${count} incoming HubRise order${count === 1 ? '' : 's'} need handling in OrderLine`}
    >
      <ExclamationTriangleIcon className={`h-5 w-5 ${tone.pulse ? 'animate-pulse' : ''}`} />
      <span className={tone.pulse ? 'animate-pulse' : ''}>
        {count} {count === 1 ? 'order needs' : 'orders need'} to be handled in OrderLine
        {ageLabel && <span className="ml-2 opacity-75">· oldest {ageLabel} ago</span>}
      </span>
      <span className="ml-2 text-xs font-semibold underline underline-offset-2">
        Open list →
      </span>
    </button>
  )
}

export default IncomingOrdersBanner
